"use strict";
import {
  useCreateWallet,
  useExportWallet,
  useLogin,
  useLoginWithOAuth,
  useLogout,
  useMfaEnrollment,
  usePrivy,
  useWallets
} from "@privy-io/react-auth";
import { useSmartWallets } from "@privy-io/react-auth/smart-wallets";
import { PRIVY_CONNECTOR_ID, privyEmbeddedConnector } from "components/Web3Provider/privy/privyConnector";
import { createSelfFundedScwSender } from "components/Web3Provider/privy/selfFundedScwClient";
import { createTopazScwProvider } from "components/Web3Provider/privy/topazScwProvider";
import { TOPAZ_SCW_CONNECTOR_ID, topazScwConnector } from "components/Web3Provider/privy/topazScwConnector";
import { walletModeAtom } from "components/Web3Provider/privy/walletMode";
import { useAtom } from "jotai";
import { createContext, useCallback, useContext, useEffect, useRef } from "react";
import { useAccount, useConnect, useDisconnect } from "wagmi";
import { bsc } from "wagmi/chains";
export function isTopazIdConnectorId(id) {
  return id === PRIVY_CONNECTOR_ID || id === TOPAZ_SCW_CONNECTOR_ID;
}
const PrivyAuthContext = createContext(null);
export function usePrivyAuth() {
  return useContext(PrivyAuthContext);
}
export function PrivyWagmiBridge({ children }) {
  const { ready, authenticated, user } = usePrivy();
  const { logout: privyLogout } = useLogout();
  const { createWallet } = useCreateWallet();
  const { exportWallet } = useExportWallet();
  const { showMfaEnrollmentModal } = useMfaEnrollment();
  const { wallets } = useWallets();
  const { client: smartWalletClient } = useSmartWallets();
  const { connectAsync } = useConnect();
  const { disconnectAsync } = useDisconnect();
  const { connector, isConnected } = useAccount();
  const [walletMode, setWalletMode] = useAtom(walletModeAtom);
  const linking = useRef(false);
  const creating = useRef(false);
  const { initOAuth, loading: oauthLoading } = useLoginWithOAuth({
    onComplete: () => {
      if (isConnected && !isTopazIdConnectorId(connector?.id)) {
        void disconnectAsync();
      }
    }
  });
  const { login } = useLogin({
    onComplete: () => {
      if (isConnected && !isTopazIdConnectorId(connector?.id)) {
        void disconnectAsync();
      }
    }
  });
  const embedded = wallets.find((w) => w.connectorType === "embedded");
  const embeddedAddress = embedded?.address;
  const smartWalletAddress = user?.smartWallet?.address;
  const smartWalletReady = Boolean(smartWalletClient && smartWalletAddress);
  const useSmartConnector = walletMode === "smart" && smartWalletReady;
  const targetConnectorId = useSmartConnector ? TOPAZ_SCW_CONNECTOR_ID : PRIVY_CONNECTOR_ID;
  const userHasEmbedded = Boolean(
    user?.linkedAccounts?.some((a) => a.type === "wallet" && a.connectorType === "embedded")
  );
  useEffect(() => {
    if (!ready || !authenticated) {
      return;
    }
    if (embedded || userHasEmbedded || creating.current) {
      return;
    }
    creating.current = true;
    void (async () => {
      try {
        await createWallet();
      } catch (err) {
        const benign = err instanceof Error && err.message.toLowerCase().includes("already has an embedded wallet");
        if (!benign) {
          console.error("Privy embedded wallet creation failed", err);
        }
      } finally {
        creating.current = false;
      }
    })();
  }, [ready, authenticated, embedded, userHasEmbedded, createWallet]);
  useEffect(() => {
    if (!ready || !authenticated || !embedded) {
      return;
    }
    if (isConnected && connector?.id && !isTopazIdConnectorId(connector.id)) {
      return;
    }
    if (isConnected && connector?.id === targetConnectorId) {
      return;
    }
    if (linking.current) {
      return;
    }
    linking.current = true;
    void (async () => {
      try {
        await embedded.switchChain(bsc.id);
        const embeddedProvider = await embedded.getEthereumProvider();
        if (isConnected && isTopazIdConnectorId(connector?.id)) {
          await disconnectAsync();
        }
        const nextConnector = useSmartConnector && smartWalletClient ? topazScwConnector(
          // Reads go through the embedded wallet's provider (already on BSC); writes/signatures
          // go through the smart-wallet client as gas-sponsored UserOperations. On a paymaster
          // failure the provider lazily falls back to the smart wallet self-paying gas — reusing
          // Privy's own account object (no reconstruction) in a paymaster-less client.
          createTopazScwProvider({
            client: smartWalletClient,
            readProvider: embeddedProvider,
            getSelfFundedSender: () => createSelfFundedScwSender({
              account: smartWalletClient?.account,
              readProvider: embeddedProvider,
              expectedAddress: smartWalletAddress
            })
          })
        ) : privyEmbeddedConnector(embeddedProvider);
        await connectAsync({ connector: nextConnector, chainId: bsc.id });
      } catch (err) {
        console.error("Failed to link Topaz ID wallet to wagmi", err);
      } finally {
        linking.current = false;
      }
    })();
  }, [
    ready,
    authenticated,
    embedded,
    embeddedAddress,
    targetConnectorId,
    useSmartConnector,
    smartWalletClient,
    // Captured by the self-funded fallback's getSelfFundedSender closure (expectedAddress). Without
    // it the connector could be built with a stale address; the early-returns above keep the re-run
    // idempotent when nothing else changed.
    smartWalletAddress,
    isConnected,
    connector?.id,
    connectAsync,
    disconnectAsync
  ]);
  useEffect(() => {
    if (!ready) {
      return;
    }
    if (!authenticated && isConnected && isTopazIdConnectorId(connector?.id)) {
      void disconnectAsync();
    }
  }, [ready, authenticated, isConnected, connector?.id, disconnectAsync]);
  const logout = useCallback(async () => {
    if (isTopazIdConnectorId(connector?.id)) {
      await disconnectAsync();
    }
    await privyLogout();
  }, [connector?.id, disconnectAsync, privyLogout]);
  const loginWithGoogle = useCallback(() => initOAuth({ provider: "google" }), [initOAuth]);
  const loginWithWallet = useCallback(() => login({ loginMethods: ["wallet"] }), [login]);
  const exportPrivateKey = useCallback(async () => {
    if (!embeddedAddress) {
      return;
    }
    await exportWallet({ address: embeddedAddress });
  }, [exportWallet, embeddedAddress]);
  return <PrivyAuthContext.Provider
    value={{
      ready,
      authenticated,
      userId: user?.id,
      email: user?.email?.address,
      logout,
      embeddedAddress,
      loginWithGoogle,
      loginWithWallet,
      oauthLoading,
      exportPrivateKey,
      mfaMethods: user?.mfaMethods ?? [],
      enrollMfa: showMfaEnrollmentModal,
      walletMode,
      setWalletMode,
      smartWalletAddress,
      smartWalletReady
    }}
  >
      {children}
    </PrivyAuthContext.Provider>;
}
